import _extends from "@babel/runtime/helpers/extends";
import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import * as React from 'react';
import styled from 'styled-components';
import { useTheme } from '@material-ui/core';
import Typography from '../Typography';
import * as logos from './logos';

var getBrandColor = function getBrandColor(type) {
  var types = {
    // Fallbacks
    ACH: '#CCCCCC',
    BankTransfer: '#CCCCCC',
    CreditCardReferenceTransaction: '#CCCCCC',
    // End fallbacks
    Argencard: '#EF1A21',
    Cencosud: '#0A5995',
    Cabal: '#03183C',
    TarjetaShopping: '#FEC328',
    Nativa: '#1D3A6B',
    Naranja: '#FF6600',
    Hipercard: '#B3131B',
    Elo: '#000000',
    Diners: '#0D4E97',
    CUP: '#E21836',
    JCB: '#114B9B',
    Discover: '#E65C00',
    AmericanExpress: '#006FCF',
    MasterCard: '#CC0000',
    Visa: '#1A1F71',
    Electron: '#1A1F71',
    Maestro: '#EC0015',
    PayPal: '#3B7BBF'
  };
  return type ? types[type] : '#CCCCCC';
};

var StyledPaymentCard = styled.div.withConfig({
  displayName: "PaymentCard__StyledPaymentCard",
  componentId: "sc-2eic7o-0"
})(["background-color:", ";color:", ";border-radius:", "px;display:flex;height:200px;padding:", "px;width:320px;"], function (_ref) {
  var $type = _ref.$type;
  return getBrandColor($type);
}, function (_ref2) {
  var $type = _ref2.$type,
      $theme = _ref2.$theme;
  return $theme.palette.getContrastText(getBrandColor($type));
}, function (_ref3) {
  var $theme = _ref3.$theme;
  return $theme.spacing(1);
}, function (_ref4) {
  var $theme = _ref4.$theme;
  return $theme.spacing(3);
});
var StyledPaymentCardInterior = styled.div.withConfig({
  displayName: "PaymentCard__StyledPaymentCardInterior",
  componentId: "sc-2eic7o-1"
})(["display:flex;flex-direction:column;justify-content:space-between;"]);
var StyledPaymentCardHeader = styled.div.withConfig({
  displayName: "PaymentCard__StyledPaymentCardHeader",
  componentId: "sc-2eic7o-2"
})(["display:flex;justify-content:space-between;line-height:14px;"]);

var padNumber = function padNumber(num) {
  if (num < 10) {
    return "0".concat(num);
  }

  return String(num).slice(-2);
};
/*
 * Input: addGaps("************1234", [4, 8, 12])
 * Output: "**** **** **** 1234"
 */


var addGaps = function addGaps(value, gaps) {
  if (!gaps || !gaps.length) {
    return value;
  }

  var newGaps = [0].concat(_toConsumableArray(gaps));
  return newGaps.reduce(function (prevVal, currVal, index) {
    var start = currVal;
    var end = index === newGaps.length - 1 ? value.length : newGaps[index + 1];
    var next = value.slice(start, end);

    if (!prevVal) {
      return next;
    }

    return "".concat(prevVal, " ").concat(next);
  }, '');
};

var getGapsForType = function getGapsForType() {
  var type = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 'BankTransfer';
  var types = {
    ACH: [],
    AmericanExpress: [4, 10],
    BankTransfer: [],
    CreditCardReferenceTransaction: [],
    CUP: [4, 8, 12],
    Diners: [4, 10],
    Discover: [4, 8, 12],
    Elo: [4, 8, 12],
    Hipercard: [4, 8, 12],
    JCB: [4, 8, 12],
    Maestro: [4, 8, 12],
    MasterCard: [4, 8, 12],
    PayPal: [],
    Visa: [4, 8, 12],
    // TODO: find spacing rules for below types
    Argencard: [],
    Cabal: [],
    Cencosud: [],
    Electron: [],
    Naranja: [],
    Nativa: [],
    TarjetaShopping: []
  };
  return types[type];
};

var isCreditOrDebitType = function isCreditOrDebitType(type) {
  return ['ACH', 'BankTransfer', 'CreditCardReferenceTransaction', 'PayPal'].indexOf("".concat(type)) === -1;
};

var formatCardNumber = function formatCardNumber(type, number) {
  if (!number) {
    return null;
  }

  var isCreditOrDebit = isCreditOrDebitType(type);

  if (!isCreditOrDebit) {
    return number;
  }

  return addGaps(number, getGapsForType(type)).replace(/\*/g, '●');
};

var StyledCardDigits = styled.div.withConfig({
  displayName: "PaymentCard__StyledCardDigits",
  componentId: "sc-2eic7o-3"
})(["margin-top:", "px;font-family:'Source Code Pro';"], function (_ref5) {
  var $theme = _ref5.$theme;
  return $theme.spacing(4);
});

var getCardLogo = function getCardLogo() {
  var _logos$type;

  var type = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 'BankTransfer';
  return ((_logos$type = logos[type]) === null || _logos$type === void 0 ? void 0 : _logos$type.call(logos)) || null;
};

var PaymentCard = function PaymentCard(_ref6) {
  var cardholderName = _ref6.cardholderName,
      cardIdentifier = _ref6.cardIdentifier,
      e2e = _ref6.e2e,
      expireMonth = _ref6.expireMonth,
      expireYear = _ref6.expireYear,
      _ref6$type = _ref6.type,
      type = _ref6$type === void 0 ? 'BankTransfer' : _ref6$type;
  var theme = useTheme();
  return /*#__PURE__*/React.createElement(StyledPaymentCard, _extends({}, e2e && {
    'data-e2e': e2e
  }, {
    $type: type,
    $theme: theme
  }), /*#__PURE__*/React.createElement(StyledPaymentCardInterior, null, /*#__PURE__*/React.createElement(StyledPaymentCardHeader, null, getCardLogo(type)), /*#__PURE__*/React.createElement(Typography, {
    variant: "h6",
    component: "div"
  }, /*#__PURE__*/React.createElement(StyledCardDigits, _extends({
    $theme: theme
  }, e2e && {
    'data-e2e': "".concat(e2e, "-digits")
  }), formatCardNumber(type, cardIdentifier))), /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(Typography, _extends({
    variant: "subtitle1",
    component: "div"
  }, e2e && {
    e2e: "".concat(e2e, "-name")
  }), cardholderName), /*#__PURE__*/React.createElement(Typography, _extends({
    variant: "caption"
  }, e2e && {
    e2e: "".concat(e2e, "-expiration")
  }), expireMonth && expireYear ? /*#__PURE__*/React.createElement(React.Fragment, null, "Expires ", padNumber(expireMonth), "/", padNumber(expireYear)) : 'No Expiration'))));
};

export default PaymentCard;

try {
  PaymentCard.__docgenInfo = {
    description: '',
    displayName: 'PaymentCard',
    methods: [],
    props: {
      cardIdentifier: {
        defaultValue: null,
        description: 'Expected formats:\n' + '- (Credit/debit cards): asterisk mask, with suffixed digits, ex: ************1234\n' + '- (PayPal): e-mail address: e.g., foobar@gmail.com',
        name: 'cardIdentifier',
        required: false,
        type: {
          name: 'string'
        }
      },
      cardholderName: {
        defaultValue: null,
        description: '',
        name: 'cardholderName',
        required: true,
        type: {
          name: 'string'
        }
      },
      e2e: {
        defaultValue: null,
        description: '',
        name: 'e2e',
        required: false,
        type: {
          name: 'string'
        }
      },
      expireMonth: {
        defaultValue: null,
        description: 'Numbers 1-12 (January-December). Values < 10 are padded.',
        name: 'expireMonth',
        required: false,
        type: {
          name: 'number'
        }
      },
      expireYear: {
        defaultValue: null,
        description: 'Calendar Number. Values < 10 will be padded.',
        name: 'expireYear',
        required: false,
        type: {
          name: 'number'
        }
      },
      type: {
        defaultValue: {
          value: 'BankTransfer'
        },
        description: 'Credit/debit cards plus ACH, BankTransfer, CreditCardReferenceTransaction, PayPal',
        name: 'type',
        required: false,
        type: {
          name: 'enum',
          raw: '"ACH" | "BankTransfer" | "CreditCardReferenceTransaction" | "PayPal" | "AmericanExpress" | "Argencard" | "Cabal" | "Cencosud" | "CUP" | "Diners" | "Discover" | "Electron" | ... 8 more ... | "Visa"',
          value: [{
            value: '"ACH"'
          }, {
            value: '"BankTransfer"'
          }, {
            value: '"CreditCardReferenceTransaction"'
          }, {
            value: '"PayPal"'
          }, {
            value: '"AmericanExpress"'
          }, {
            value: '"Argencard"'
          }, {
            value: '"Cabal"'
          }, {
            value: '"Cencosud"'
          }, {
            value: '"CUP"'
          }, {
            value: '"Diners"'
          }, {
            value: '"Discover"'
          }, {
            value: '"Electron"'
          }, {
            value: '"Elo"'
          }, {
            value: '"Hipercard"'
          }, {
            value: '"JCB"'
          }, {
            value: '"Maestro"'
          }, {
            value: '"MasterCard"'
          }, {
            value: '"Nativa"'
          }, {
            value: '"Naranja"'
          }, {
            value: '"TarjetaShopping"'
          }, {
            value: '"Visa"'
          }]
        }
      }
    }
  };
} catch (e) {}
//# sourceMappingURL=index.js.map